import { IServerProps } from 'common/types/common';
import { IProjectItemOverview } from 'common/types/projects';
import { buildAbsoluteUrl } from 'common/utils/url';
import { Layout } from 'layout';
import { GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const CTA = dynamic(() => import('common/components/Cta').then(mod => mod.CTA));
const HeroWork = dynamic(() => import('modules/work/HeroWork').then(mod => mod.HeroWork));
const WorkCaseStudiesCarousel = dynamic(() =>
  import('modules/work/WorkCaseStudiesCarousel').then(mod => mod.WorkCaseStudiesCarousel),
);

const WorkPage = () => {
  const { t } = useTranslation(['meta', 'projectItemsOverview']);

  const projectsData = t('projectItemsOverview:projectItems', {
    returnObjects: true,
    defaultValue: [],
  }) as unknown;
  const projects: IProjectItemOverview[] = Array.isArray(projectsData)
    ? (projectsData as IProjectItemOverview[])
    : [];
  const base = t('common:company.url');
  const schemaMarkup = projects.map(project => {
    const url = project.slug ? buildAbsoluteUrl(base, project.slug) : '';
    return {
      '@type': 'BlogPosting',
      mainEntityOfPage: { '@type': 'WebPage', '@id': base },
      headline: project.seo.title,
      image: project.seo.image,
      description: project.seo.description,
      author: {
        '@type': 'Person',
        name: project.author.name,
        url: project.author.name,
      },
      url,
      publisher: {
        '@type': 'Organization',
        name: 'Nordit d.o.o.',
        url: 'https://nordit.co',
        logo: {
          '@type': 'ImageObject',
          url: 'https://firebasestorage.googleapis.com/v0/b/nordit-83a0b.appspot.com/o/logo%2FNordit%20logo%20-%20primary%20white%2FNordit%20logo%20-%20primary%20white%402x.png?alt=media&token=d979ae78-414f-4fd1-bfb5-dc31ad8ce3cf',
        },
      },
      datePublished: project.date,
    };
  });
  const seo = {
    alternate: t('workList.alternate', { returnObjects: true }),
    description: t('workList.description'),
    image: t('workList.image'),
    title: t('workList.title'),
    schemaMarkup,
    slug: t('workList.slug'),
  };
  return (
    <Layout dark seo={seo}>
      <HeroWork />
      <WorkCaseStudiesCarousel />
      <CTA dark />
    </Layout>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
): Promise<IServerProps<IWorkPageProps>> => {
  const locale = await serverSideTranslations(context.locale || 'en', [
    'common',
    'meta',
    'pageWorkList',
    'projectItemsDetails',
    'projectItemsOverview',
  ]);
  return {
    props: {
      data: { referer: context.req.headers.referer || null },
      ...locale,
    },
  };
};

interface IWorkPageProps {
  data: { referer?: string | null } | Record<string, never>;
}

export default WorkPage;
